import { useMutation } from "@apollo/client";
import { H6, Switch } from "@blueprintjs/core";
import BusDot from "components/common/BusDot";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    UpdateUserColorblindModeMutation,
    UpdateUserColorblindModeMutationVariables
} from "generated/graphql";
import { loader } from "graphql.macro";
import { getColors } from "sharedStyles/capacityColorStyles";
import {
    CapacityThresholds,
    ColorMode,
    DEFAULT_CONSTRAINT_COUNT_THRESHOLD
} from "types/busFilterTypes";
import { isCurtailmentConfig } from "types/componentConfigPerRegion";
import { STORAGE_TYPE } from "types/generatorType";
import "./Legend.scss";

const UPDATE_USER_COLORBLIND_MODE = loader(
    "src/graphql/updateUserColorblindMode.graphql"
);

type LegendProps = {
    readonly hideLegendTitle?: boolean;
};

const Legend: React.FC<LegendProps> = (props) => {
    const { generator, componentConfig } = useMapViewRoutingMetadata();
    const {
        busFiltersConfiguration: { busFilters }
    } = useMapViewConfiguration();
    const user = useUserDataContext();
    const [updateColorblindMode] = useMutation<
        UpdateUserColorblindModeMutation,
        UpdateUserColorblindModeMutationVariables
    >(UPDATE_USER_COLORBLIND_MODE, {
        onError: (error) => {
            console.error("Failed to update colorblind mode:", error);
        }
    });

    const { colorMode, capacityThresholds, constraintCountThresholds } =
        busFilters;

    const isConstraintCountMode = colorMode === ColorMode.CONSTRAINT_COUNT;
    const currentThresholds = isConstraintCountMode
        ? constraintCountThresholds ?? DEFAULT_CONSTRAINT_COUNT_THRESHOLD
        : capacityThresholds;

    // When configured, the capacity legend shows English
    // descriptions instead of numeric thresholds.
    const capacityLegendLabels = isConstraintCountMode
        ? undefined
        : componentConfig.capacityLegendLabels;

    const isCurtailment = isCurtailmentConfig(componentConfig);
    const showChargingInfo =
        isCurtailment &&
        generator === STORAGE_TYPE &&
        componentConfig.showChargingSize;

    // Determine unit label: constraint count mode shows no unit, otherwise MW/MWE
    const unitLabel = isConstraintCountMode
        ? ""
        : !isCurtailment
        ? "MWE"
        : "MW";

    return (
        <div className="Legend-section-wrapper">
            {!props.hideLegendTitle && (
                <div className="Legend-header-wrapper">
                    <H6 className="Legend-h6">Legend</H6>
                </div>
            )}
            <div>
                {getLegendRow(
                    "highThreshold",
                    currentThresholds,
                    unitLabel,
                    isConstraintCountMode,
                    user.isColorblindMode,
                    capacityLegendLabels?.high
                )}
                {getLegendRow(
                    "mediumThreshold",
                    currentThresholds,
                    unitLabel,
                    isConstraintCountMode,
                    user.isColorblindMode,
                    capacityLegendLabels?.medium
                )}
                {getLegendRow(
                    "lowThreshold",
                    currentThresholds,
                    unitLabel,
                    isConstraintCountMode,
                    user.isColorblindMode,
                    capacityLegendLabels?.low
                )}
            </div>
            {showChargingInfo && (
                <div className="Legend-storage-info-text bp5-text-small bp5-text-muted">
                    Color-coded by the minimum of MW(D) and MW(C).
                </div>
            )}
            {!props.hideLegendTitle && (
                <div className="Legend-colorblind-toggle">
                    <span className="bp5-text-small">Colorblind mode</span>
                    <Switch
                        checked={user.isColorblindMode}
                        onChange={() => {
                            const newColorblindMode = !user.isColorblindMode;
                            updateColorblindMode({
                                variables: {
                                    auth0Id: user.auth0Id,
                                    colorblindMode: newColorblindMode
                                },
                                optimisticResponse: {
                                    update_users_by_pk: {
                                        __typename: "users",
                                        auth0_id: user.auth0Id,
                                        colorblind_mode: newColorblindMode
                                    }
                                }
                            });
                        }}
                    />
                </div>
            )}
        </div>
    );
};

const getLegendRow = (
    threshold: keyof CapacityThresholds,
    thresholds: CapacityThresholds,
    unitLabel: string,
    invertColors: boolean,
    isColorblindMode: boolean,
    labelOverride?: string
) => {
    // Add space before unit if there is one
    const unitSuffix = unitLabel ? ` ${unitLabel}` : "";

    const colors = getColors(isColorblindMode);

    let color;
    let label;
    if (threshold === "highThreshold") {
        // For constraint count: high = poor; for capacity: high = good
        color = invertColors ? colors.poor : colors.good;
        label = labelOverride ?? `${thresholds.highThreshold}+${unitSuffix}`;
    } else if (threshold === "mediumThreshold") {
        color = colors.fair;
        label =
            labelOverride ??
            `${thresholds.mediumThreshold}-${thresholds.highThreshold}${unitSuffix}`;
    } else {
        // For constraint count: low = good; for capacity: low = poor
        color = invertColors ? colors.good : colors.poor;
        label =
            labelOverride ??
            `${thresholds.lowThreshold}-${thresholds.mediumThreshold}${unitSuffix}`;
    }

    return (
        <div className="Legend-legend-row">
            <BusDot color={color} small={true} />
            <div className="Legend-legend-row-text">{label}</div>
        </div>
    );
};

export default Legend;
